/**
 * Autosave de la `definition` de una versión draft — una sola regla para
 * todos los editores de plantillas (artefacto, ciclo de vida, rol, flujo).
 *
 * Tras `AUTOSAVE_DELAY_MS` sin cambios, si la definición difiere de la
 * última guardada, hace `PATCH .../versions/{id}`. `preflight` (opcional)
 * detecta problemas de formato antes de llamar al backend: el estado pasa a
 * `invalid` con esos problemas, sin PATCH. Si el backend rechaza, el estado
 * es `error` con el resumen del primer error (`serverError`). Al guardar,
 * invalida el reporte de calidad de la versión (OT-0006).
 */

import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { ApiError } from '../../../api/client'
import { updateVersionDefinition, versionQualityQueryKey } from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import type { ClientValidationIssue } from './state'

const AUTOSAVE_DELAY_MS = 1500

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'invalid'

export interface ServerErrorSummary {
  code: string
  summary: string
}

/** Lo que la cabecera del editor necesita para mostrar el estado. */
export interface AutosaveState {
  saveStatus: SaveStatus
  validationIssues: ClientValidationIssue[]
  serverError: ServerErrorSummary | null
}

export const IDLE_AUTOSAVE: AutosaveState = {
  saveStatus: 'idle',
  validationIssues: [],
  serverError: null,
}

interface UseDefinitionAutosaveArgs<D> {
  templateId: string
  versionId: string
  /** La definición que se está editando. */
  definition: D
  /** La definición guardada al montar (la de la versión). */
  savedDefinition: D
  readOnly: boolean
  preflight?: ((definition: D) => ClientValidationIssue[]) | undefined
  /** Tras guardar (p.ej. para marcar el estado del editor como limpio). */
  onSaved?: (() => void) | undefined
}

export function useDefinitionAutosave<D>({
  templateId,
  versionId,
  definition,
  savedDefinition,
  readOnly,
  preflight,
  onSaved,
}: UseDefinitionAutosaveArgs<D>): AutosaveState {
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [validationIssues, setValidationIssues] = useState<ClientValidationIssue[]>([])
  const [serverError, setServerError] = useState<ServerErrorSummary | null>(null)
  const [lastSaved, setLastSaved] = useState<string>(() => JSON.stringify(savedDefinition))
  const serialized = JSON.stringify(definition)

  useEffect(() => {
    if (readOnly || !accessToken) return
    if (serialized === lastSaved) return
    const handle = window.setTimeout(() => {
      const issues = preflight?.(definition) ?? []
      if (issues.length > 0) {
        setValidationIssues(issues)
        setSaveStatus('invalid')
        return
      }
      setValidationIssues([])
      setSaveStatus('saving')
      updateVersionDefinition(accessToken, templateId, versionId, {
        definition: definition as unknown as Record<string, unknown>,
      })
        .then(() => {
          setLastSaved(serialized)
          setServerError(null)
          setSaveStatus('saved')
          void queryClient.invalidateQueries({
            queryKey: versionQualityQueryKey(templateId, versionId),
          })
          onSaved?.()
        })
        .catch((err: unknown) => {
          setServerError(summarizeServerError(err))
          setSaveStatus('error')
        })
    }, AUTOSAVE_DELAY_MS)
    return () => window.clearTimeout(handle)
    // `definition`/`preflight`/`onSaved` siguen a `serialized`: el autosave
    // se reprograma cuando cambia el contenido, no la identidad del objeto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, lastSaved, readOnly, accessToken, templateId, versionId])

  if (readOnly) return IDLE_AUTOSAVE
  // De vuelta a lo guardado (p.ej. se quitó la etapa vacía): el problema
  // que detectó el preflight ya no aplica.
  if (saveStatus === 'invalid' && serialized === lastSaved) return IDLE_AUTOSAVE
  return { saveStatus, validationIssues, serverError }
}

/**
 * Resumen legible de un error del autosave. El backend devuelve
 * `{error: {code, message, details}}` (Doc 19 §3.2); para
 * `TEMPLATE_INVALID_DEFINITION` (Pydantic), `details.errors[0]` trae
 * `{loc, msg}` con la ruta exacta del campo — el dato accionable.
 * `null` cuando no hay info útil (red, timeout): la cabecera cae al
 * texto genérico.
 */
function summarizeServerError(err: unknown): ServerErrorSummary | null {
  if (!(err instanceof ApiError)) return null
  const code = err.code
  const details = err.body?.details
  if (details && typeof details === 'object' && 'errors' in details) {
    const errors = (details as { errors?: unknown }).errors
    if (Array.isArray(errors) && errors.length > 0) {
      const first = errors[0] as { loc?: unknown[]; msg?: string }
      const loc = Array.isArray(first.loc) ? first.loc.map((p) => String(p)).join('.') : ''
      const msg = typeof first.msg === 'string' ? first.msg : ''
      if (loc || msg) {
        return { code, summary: loc && msg ? `${loc} — ${msg}` : loc || msg }
      }
    }
  }
  const detail =
    details && typeof details === 'object' && 'detail' in details
      ? String((details as { detail?: unknown }).detail ?? '')
      : ''
  return { code, summary: detail || err.body?.message || err.message }
}
